(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const activeAnimations = new Set();
  const track = animation => {
    if (!animation) return;
    activeAnimations.add(animation);
    animation.finished.catch(() => {}).finally(() => activeAnimations.delete(animation));
  };
  reduced.addEventListener('change', () => {
    if (reduced.matches) activeAnimations.forEach(animation => animation.finish());
  });
  const animateIn = (element, distance = 12, duration = 500) => {
    if (reduced.matches || !element.animate) return;
    track(element.animate([{ opacity: 0, transform: `translateY(${distance}px)` }, { opacity: 1, transform: 'translateY(0)' }], { duration, easing: 'cubic-bezier(.2,.7,.2,1)' }));
  };
  if (!customElements.get('kz-symbol-explorer')) customElements.define('kz-symbol-explorer', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      const grid = this.querySelector('.kz-symbol-grid');
      if (!grid) return;
      const buttons = [...grid.querySelectorAll('button')];
      const panels = [...this.querySelectorAll('.kz-symbol-panel')];
      const select = (index, focus = false, motion = true) => {
        buttons.forEach((button, i) => button.setAttribute('aria-pressed', String(i === index)));
        panels.forEach((panel, i) => { panel.hidden = i !== index; });
        if (focus) buttons[index].focus();
        if (motion) animateIn(panels[index], 4, 180);
      };
      buttons.forEach((button, index) => {
        button.addEventListener('click', () => select(index), options);
        button.addEventListener('keydown', event => {
          let next = index;
          if (event.key === 'ArrowRight') next = (index + 1) % buttons.length;
          else if (event.key === 'ArrowLeft') next = (index - 1 + buttons.length) % buttons.length;
          else if (event.key === 'ArrowDown') next = (index + 4) % buttons.length;
          else if (event.key === 'ArrowUp') next = (index - 4 + buttons.length) % buttons.length;
          else if (event.key === 'Home') next = 0;
          else if (event.key === 'End') next = buttons.length - 1;
          else return;
          event.preventDefault(); select(next, true);
        }, options);
      });
      grid.hidden = false; this.classList.add('kz-enhanced');
      select(Math.max(0, buttons.findIndex(button => button.dataset.key === 'lotus')), false, false);
      this.addEventListener('shopify:block:select', event => {
        const index = panels.findIndex(panel => panel.contains(event.target));
        if (index >= 0) select(index, false, false);
      }, options);
    }
    disconnectedCallback() { this.controller?.abort(); this.controller = null; }
  });
  if (!customElements.get('kz-reflections')) customElements.define('kz-reflections', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const quotes = [...this.querySelectorAll('blockquote')];
      if (!quotes.length) return;
      let index = 0;
      const show = motion => {
        quotes.forEach((quote, i) => { quote.hidden = i !== index; });
        this.querySelector('.kz-quote-count').textContent = `${String(index + 1).padStart(2, '0')} / ${String(quotes.length).padStart(2, '0')}`;
        if (motion) animateIn(quotes[index], 4, 180);
      };
      show(false); this.querySelector('.kz-quote-controls').hidden = false;
      this.querySelector('button').addEventListener('click', () => { index = (index + 1) % quotes.length; show(true); }, { signal: this.controller.signal });
    }
    disconnectedCallback() { this.controller?.abort(); this.controller = null; }
  });
  if (!customElements.get('kz-brand-hero')) customElements.define('kz-brand-hero', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.pointerMedia = window.matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)');
      this.landscape = this.querySelector('.kz-brand-landscape');
      this.mist = this.querySelector('.kz-brand-mist');
      this.ornament = this.querySelector('.kz-brand-ornament');
      this.light = this.querySelector('.kz-cursor-light');
      this.aura = this.querySelector('.kz-cursor-aura');
      this.depth = Math.max(0, Math.min(12, Number(this.dataset.depth ?? 6) || 0));
      this.visible = false; this.frame = 0; this.lastTime = 0;
      this.pointerInside = false; this.bounds = null;
      this.x = this.y = this.auraX = this.auraY = this.targetX = this.targetY = 0;
      this.shiftX = this.shiftY = this.targetShiftX = this.targetShiftY = 0;
      this.allowed = () => this.dataset.motion === 'true' && !reduced.matches && this.pointerMedia.matches && this.visible && !document.hidden;
      this.reset = () => {
        cancelAnimationFrame(this.frame); this.frame = 0; this.lastTime = 0;
        this.pointerInside = false; this.bounds = null;
        this.shiftX = this.shiftY = this.targetShiftX = this.targetShiftY = 0;
        this.classList.remove('kz-pointer-active');
        [this.landscape, this.mist, this.ornament].forEach(layer => layer?.style.removeProperty('transform'));
      };
      const translate = (layer, x, y, centered = false) => {
        if (layer) layer.style.transform = `translate3d(${x.toFixed(2)}px,${y.toFixed(2)}px,0)${centered ? ' translate(-50%,-50%)' : ''}`;
      };
      this.tick = time => {
        this.frame = 0;
        if (!this.allowed()) { this.reset(); return; }
        const elapsed = this.lastTime ? Math.min(40, Math.max(1, time - this.lastTime)) : 16.67;
        this.lastTime = time;
        const near = 1 - Math.exp(-elapsed / 85), far = 1 - Math.exp(-elapsed / 210), depth = 1 - Math.exp(-elapsed / 150);
        this.x += (this.targetX - this.x) * near; this.y += (this.targetY - this.y) * near;
        this.auraX += (this.targetX - this.auraX) * far; this.auraY += (this.targetY - this.auraY) * far;
        this.shiftX += (this.targetShiftX - this.shiftX) * depth; this.shiftY += (this.targetShiftY - this.shiftY) * depth;
        translate(this.light, this.x, this.y, true); translate(this.aura, this.auraX, this.auraY, true);
        translate(this.landscape, -this.shiftX * this.depth, -this.shiftY * this.depth);
        translate(this.mist, this.shiftX * this.depth * 2, this.shiftY * this.depth * 2);
        translate(this.ornament, this.shiftX * this.depth * 2.5, this.shiftY * this.depth * 2.5);
        const lightDistance = Math.abs(this.targetX - this.x) + Math.abs(this.targetY - this.y) + Math.abs(this.targetX - this.auraX) + Math.abs(this.targetY - this.auraY);
        const depthDistance = Math.abs(this.targetShiftX - this.shiftX) + Math.abs(this.targetShiftY - this.shiftY);
        if (lightDistance > .2 || depthDistance > .001) this.frame = requestAnimationFrame(this.tick);
        else this.lastTime = 0;
      };
      this.addEventListener('pointermove', event => {
        if (event.pointerType === 'touch' || !this.allowed()) return;
        if (!this.pointerInside) this.bounds = this.getBoundingClientRect();
        const rect = this.bounds;
        if (!rect?.width || !rect.height) return;
        this.targetX = Math.max(0, Math.min(rect.width, event.clientX - rect.left));
        this.targetY = Math.max(0, Math.min(rect.height, event.clientY - rect.top));
        this.targetShiftX = (this.targetX / rect.width - .5) * 2;
        this.targetShiftY = (this.targetY / rect.height - .5) * 2;
        if (!this.pointerInside) {
          this.x = this.auraX = this.targetX; this.y = this.auraY = this.targetY;
          translate(this.light, this.x, this.y, true); translate(this.aura, this.auraX, this.auraY, true);
        }
        this.pointerInside = true; this.classList.add('kz-pointer-active');
        if (!this.frame) this.frame = requestAnimationFrame(this.tick);
      }, { ...options, passive: true });
      this.addEventListener('pointerleave', () => {
        this.pointerInside = false; this.bounds = null;
        this.targetShiftX = this.targetShiftY = 0;
        this.classList.remove('kz-pointer-active');
        if (!this.frame && this.allowed()) this.frame = requestAnimationFrame(this.tick);
      }, options);
      document.addEventListener('visibilitychange', this.reset, options);
      window.addEventListener('resize', this.reset, { ...options, passive: true });
      window.addEventListener('scroll', this.reset, { ...options, passive: true });
      reduced.addEventListener('change', this.reset, options);
      this.pointerMedia.addEventListener('change', this.reset, options);
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          this.visible = entries[0].isIntersecting;
          if (!this.visible) this.reset();
        }, { threshold: 0 });
        this.observer.observe(this);
      }
      if (this.dataset.motion === 'true' && !reduced.matches) {
        this.querySelectorAll('.kz-title-mask > span').forEach((line, index) => {
          track(line.animate?.([{ transform: 'translateY(105%)' }, { transform: 'translateY(0)' }], { duration: 650, delay: index * 70, easing: 'cubic-bezier(.2,.7,.2,1)' }));
        });
      }
    }
    disconnectedCallback() { this.reset?.(); this.observer?.disconnect(); this.controller?.abort(); this.controller = null; }
  });
  if ('IntersectionObserver' in window) {
    const seen = new WeakSet();
    const watched = new Set();
    const observer = new IntersectionObserver(entries => entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      observer.unobserve(entry.target); watched.delete(entry.target);
      if (seen.has(entry.target)) return;
      seen.add(entry.target); animateIn(entry.target, 16, 500);
    }), { threshold: .08, rootMargin: '0px 0px -24px 0px' });
    const scan = root => {
      const elements = [...root.querySelectorAll('[data-kz-reveal]')];
      if (root.matches?.('[data-kz-reveal]')) elements.push(root);
      ['__collection_list_3ejxRy', '__product_list_YidTcf', '__media_with_content_HP7Qpp'].forEach(suffix => {
        const selector = `[id$="${suffix}"]`;
        const section = root.matches?.(selector) ? root : root.querySelector(selector);
        if (section) elements.push(section);
      });
      elements.forEach(element => { if (!seen.has(element)) { watched.add(element); observer.observe(element); } });
    };
    scan(document);
    document.addEventListener('shopify:section:load', event => scan(event.target));
    document.addEventListener('shopify:section:unload', event => watched.forEach(element => {
      if (event.target === element || event.target.contains(element)) { observer.unobserve(element); watched.delete(element); }
    }));
  }
})();
